import React from 'react';
import { useWizard } from '../../context/WizardContext';
import { useSubscription } from '../../context/SubscriptionContext';
import { Lock } from 'lucide-react';

const ProgressBar = () => {
  const { currentWizard, currentStep, highestStep, goToStep, wizardAComplete, wizardBComplete, wizardCComplete, wizardEComplete } = useWizard();
  const { triggerUpgradeModal, getWizardMaxStep, currentTier } = useSubscription();
  
  const maxAccessibleStep = getWizardMaxStep(currentWizard);
  const isFreeUser = currentTier === 'free';
  const freeStepLimit = 4;
  
  const wizardAStepLabels = [
    'Research Fundamentals',
    'Philosophical Dimensions',
    'Research Paradigm',
    'Theoretical Approach',
    'Research Method',
    'Research Strategy',
    'Time Horizon',
    'Data Techniques'
  ];

  const wizardBStepLabels = [
    'Introduction',
    'Analysis Approach',
    'Qualitative Methods',
    'Quantitative Methods',
    'Data Requirements',
    'Wizard G Integration',
    'Summary Review'
  ];

  const wizardCStepLabels = [
    'Extract Constructs',
    'Analysis Levels',
    'Theoretical Foundations',
    'Theory Linkage',
    'Relationship Mapping',
    'Assumptions',
    'Propositions',
    'Framework Validation',
    'Export Framework',
    'Framework Write-up'
  ];

  const wizardEStepLabels = [
    'Ethics Overview',
    'Research Team',
    'Project Synopsis',
    'Participants',
    'Recruitment Strategy',
    'Data Collection',
    'Data Management',
    'Risk Assessment',
    'Special Considerations',
    'Dissemination Plan'
  ];

  if (currentWizard === 'GF' || currentWizard === 'D' || currentWizard === 'F') {
    return null;
  }

  const stepLabels = currentWizard === 'A' ? wizardAStepLabels : 
                     currentWizard === 'B' ? wizardBStepLabels : 
                     currentWizard === 'C' ? wizardCStepLabels : 
                     currentWizard === 'E' ? wizardEStepLabels : wizardAStepLabels;
  const totalSteps = stepLabels.length;
  const isWizardComplete = currentWizard === 'A' ? wizardAComplete : 
                           currentWizard === 'B' ? wizardBComplete : 
                           currentWizard === 'C' ? wizardCComplete : 
                           currentWizard === 'E' ? wizardEComplete : false;
  
  const wizardDisplayLetter = currentWizard === 'A' ? 'B' :
                              currentWizard === 'B' ? 'C' :
                              currentWizard === 'C' ? 'D' :
                              currentWizard === 'E' ? 'F' : currentWizard;

  const wizardName = currentWizard === 'A' ? 'Research Methodology' : 
                     currentWizard === 'B' ? 'Data Analysis' : 
                     currentWizard === 'C' ? 'Framework Builder' : 
                     currentWizard === 'E' ? 'Ethics Approval' : '';

  const isStepLocked = (stepIndex) => stepIndex > maxAccessibleStep;

  const handleStepClick = (stepIndex) => {
    if (isStepLocked(stepIndex)) {
      triggerUpgradeModal(`wizard${currentWizard}`);
      return;
    }
    goToStep(stepIndex);
  };

  const getStepStatus = (stepIndex) => {
    if (isStepLocked(stepIndex)) return 'locked';
    if (stepIndex === currentStep && !isWizardComplete) return 'current';
    if (stepIndex < currentStep || isWizardComplete) return 'completed';
    if (stepIndex <= highestStep) return 'completed';
    return 'upcoming';
  };

  const freeStepsUsed = Math.min(currentStep + 1, freeStepLimit);
  const showFreeStepIndicator = isFreeUser && maxAccessibleStep < totalSteps - 1;

  return (
    <div className="progress-container">
      <div className="progress-header">
        <span className="progress-label">
          Wizard {wizardDisplayLetter}: {wizardName}
        </span>
        <span className="progress-steps">
          {isWizardComplete
            ? 'Complete — Click any step to edit'
            : currentStep > highestStep
            ? `Previewing Step ${currentStep + 1} of ${totalSteps} (read-only)`
            : `Step ${currentStep + 1} of ${totalSteps}`}
        </span>
      </div>
      
      {showFreeStepIndicator && (
        <div className="free-steps-indicator">
          <div className="free-steps-bar">
            <div 
              className="free-steps-fill" 
              style={{ width: `${(freeStepsUsed / freeStepLimit) * 100}%` }}
            />
          </div>
          <span className="free-steps-text">
            {freeStepsUsed >= freeStepLimit 
              ? `You've used all ${freeStepLimit} free steps - Upgrade for full access`
              : `You've used ${freeStepsUsed} of ${freeStepLimit} free steps`
            }
          </span>
        </div>
      )}
      
      <div className="step-indicators">
        {stepLabels.map((label, index) => {
          const status = getStepStatus(index);
          const locked = isStepLocked(index);
          const isAhead = !isWizardComplete && index > highestStep;
          const isClickable = !locked;
          
          return (
            <div
              key={index}
              className={`step-indicator ${status} ${isClickable ? 'clickable' : ''} ${locked ? 'locked' : ''} ${isAhead && !locked ? 'preview-step' : ''}`}
              onClick={() => handleStepClick(index)}
              title={
                locked
                  ? `Upgrade to unlock ${label}`
                  : isAhead
                  ? `Preview Step ${index + 1}: ${label} (read-only)`
                  : `Go to ${label}`
              }
            >
              <div className="step-circle">
                {locked ? <Lock size={12} /> : status === 'completed' ? '✓' : index + 1}
              </div>
              <span className="step-label">{label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ProgressBar;
